import JSBI from 'jsbi'
import { AggregatorQuote, QuoteProvider, QuoteRequest, RouteHop } from '../types'
import { buildQuery, endpointFor, getJson, hasQuoteProxy } from '../http'
import { feeAsPercent, revenueConfig } from '../revenue'

/**
 * 1inch Swap API (v6).
 *
 * Revenue rides on `referrer` + `fee`, where `fee` is a percentage (1 = 1%), capped by
 * 1inch at 3%. Deepest BNB Chain liquidity of the three sources here, but it requires a
 * paid API key at production volume - flagged as an operating cost, not a free add.
 *
 * Key lives on the proxy. Without one this provider is skipped rather than 401ing.
 *
 * FIELD NAMES: verify `normalise` against a live response when the key is issued.
 */

const DIRECT_BASE = 'https://api.1inch.dev/swap/v6.0'

const SUPPORTED_CHAINS = [56]

interface OneInchProtocol {
  name?: string
  part?: number
}

interface OneInchResponse {
  dstAmount?: string
  srcAmount?: string
  gas?: string | number
  tx?: { to?: string; data?: string; value?: string; gas?: string | number }
  protocols?: OneInchProtocol[][][]
  description?: string
  error?: string
}

function applySlippage(amount: string, slippageBps: number): string {
  try {
    const out = JSBI.BigInt(amount)
    const keep = JSBI.BigInt(10_000 - slippageBps)
    return JSBI.divide(JSBI.multiply(out, keep), JSBI.BigInt(10_000)).toString()
  } catch {
    return '0'
  }
}

/**
 * 1inch nests protocols as route -> hop -> venue, each venue carrying a `part`
 * percentage. Flattened into a single share-per-venue view for the UI.
 */
function extractRoute(response: OneInchResponse): RouteHop[] {
  const totals = new Map<string, number>()

  response.protocols?.forEach((route) =>
    route.forEach((hop) =>
      hop.forEach((venue) => {
        const name = venue.name ?? 'Unknown'
        totals.set(name, (totals.get(name) ?? 0) + (venue.part ?? 0))
      })
    )
  )

  return Array.from(totals.entries())
    .map(([source, part]) => ({ source, portionBps: Math.round(part * 100) }))
    .filter((hop) => hop.portionBps > 0)
    .sort((a, b) => b.portionBps - a.portionBps)
}

export function normalise(response: OneInchResponse, request: QuoteRequest): AggregatorQuote {
  if (response.error) throw new Error(response.description ?? response.error)
  if (!response.dstAmount) throw new Error('No route found')

  return {
    providerId: '1inch',
    providerLabel: '1inch',
    sellAmount: response.srcAmount ?? request.sellAmount,
    buyAmount: response.dstAmount,
    // 1inch does not return a guaranteed minimum; it is derived from slippage.
    minBuyAmount: applySlippage(response.dstAmount, request.slippageBps),
    estimatedGas: response.tx?.gas !== undefined ? String(response.tx.gas) : undefined,
    allowanceTarget: response.tx?.to,
    route: extractRoute(response),
    fee: revenueConfig.enabled
      ? { bps: revenueConfig.feeBps, recipient: revenueConfig.feeRecipient }
      : undefined,
    transaction:
      response.tx?.to && response.tx?.data
        ? { to: response.tx.to, data: response.tx.data, value: response.tx.value ?? '0' }
        : undefined,
    raw: response,
  }
}

export const oneInchProvider: QuoteProvider = {
  id: '1inch',
  label: '1inch',

  supportsChain: (chainId) => SUPPORTED_CHAINS.includes(chainId),

  isConfigured: () => hasQuoteProxy,

  async fetchQuote(request, signal) {
    // /swap returns calldata and needs a wallet; /quote is price-only.
    const path = request.taker ? `/${request.chainId}/swap` : `/${request.chainId}/quote`

    const query = buildQuery({
      src: request.sellToken.address,
      dst: request.buyToken.address,
      amount: request.sellAmount,
      from: request.taker,
      origin: request.taker,
      slippage: request.slippageBps / 100,
      includeProtocols: 'true',
      referrer: revenueConfig.enabled ? revenueConfig.feeRecipient : undefined,
      fee: revenueConfig.enabled ? feeAsPercent() : undefined,
    })

    const url = `${endpointFor('1inch', DIRECT_BASE, path)}?${query}`
    return normalise(await getJson<OneInchResponse>(url, signal), request)
  },
}
